import { useState, useEffect, useMemo } from 'react';
import Chip from '../components/Chip';
import { ordersApi } from '../services/api';
import { useApp } from '../AppContext';

const CT_ICONS = {
  IDENTITY: '🪖', ADDRESS: '🏠', EDUCATION: '🎓', EMPLOYMENT: '💼',
  CRIMINAL: '🚨', REFERENCE: '👥', CREDIT: '💳', DRUG: '🧪', GLOBAL: '🌐',
};

// Same allow-list/size cap as New Order → Direct Order's document upload.
const ALLOWED_DOC_EXTS = ['.xlsx', '.xls', '.doc', '.docx', '.pdf', '.zip', '.rar', '.png', '.jpg', '.jpeg'];
const MAX_DOC_SIZE = 100 * 1024 * 1024;

function ctIcon(ct) { return CT_ICONS[(ct || '').toUpperCase()] || '📋'; }

function caseTitle(row) {
  return row.case_type;
  // return row.stype
  //   || row.sub_type
  //   || row.identitysubtype
  //   || row.profidentitysubtype
  //   || row.case_number
  //   || (row.case_type || 'Check').replace(/_/g, ' ');
}

function caseDetails(row) {
  const ct = (row.case_type || '').toUpperCase();
  const p = [];

  if (ct === 'IDENTITY') {
    const docType = row.stype || row.identitysubtype || row.profidentitysubtype;
    const docNum  = row.sub_entity || row.identitysubtypevalue || row.profidentitysubtypevalue;
    if (docType) p.push({ key: 'Document Type', val: docType });
    if (docNum)  p.push({ key: 'Document No.',  val: docNum  });
    if (row.identityjurisdiction) p.push({ key: 'Jurisdiction', val: row.identityjurisdiction });
  } else if (ct === 'ADDRESS') {
    const addr = [row.entityaddress1, row.entityaddress2, row.entityaddress3,
                  row.entitycity, row.entitystate, row.entitypostalcode, row.entitycountry]
                  .filter(Boolean).join(', ');
    if (addr) p.push({ key: 'Address',   val: addr              });
    if (row.entitystayfrom) p.push({ key: 'Stay From', val: row.entitystayfrom });
    if (row.entitystayto)   p.push({ key: 'Stay To',   val: row.entitystayto   });
  } else if (ct === 'EDUCATION') {
    if (row.entityuniversity) p.push({ key: 'University', val: row.entityuniversity });
    if (row.entitycollege)    p.push({ key: 'College',    val: row.entitycollege    });
    if (row.entitydegree)     p.push({ key: 'Degree',     val: row.entitydegree     });
    if (row.entitymajor)      p.push({ key: 'Major',      val: row.entitymajor      });
    if (row.entityfromdate)   p.push({ key: 'From',       val: row.entityfromdate   });
    if (row.entitytodate)     p.push({ key: 'To',         val: row.entitytodate     });
    if (row.entitypercentage) p.push({ key: 'Percentage', val: String(row.entitypercentage) });
  } else if (ct === 'EMPLOYMENT') {
    if (row.entityemployer)    p.push({ key: 'Employer',     val: row.entityemployer    });
    if (row.entitylocation)    p.push({ key: 'Location',     val: row.entitylocation    });
    if (row.entityjobtitle)    p.push({ key: 'Job Title',    val: row.entityjobtitle    });
    if (row.entitydept)        p.push({ key: 'Department',   val: row.entitydept        });
    if (row.entityfromdate)    p.push({ key: 'From',         val: row.entityfromdate    });
    if (row.entitytodate)      p.push({ key: 'To',           val: row.entitytodate      });
    if (row.entityleavereason) p.push({ key: 'Leave Reason', val: row.entityleavereason });
  } else if (ct === 'CRIMINAL') {
    if (row.criminalrecordfound) p.push({ key: 'Record Found',   val: row.criminalrecordfound  });
    if (row.policestationname)   p.push({ key: 'Police Station', val: row.policestationname    });
    if (row.policeofficialname)  p.push({ key: 'Officer',        val: row.policeofficialname   });
  } else if (ct === 'REFERENCE') {
    if (row.entityreferencename) p.push({ key: 'Name',     val: row.entityreferencename });
    if (row.entitycompanyname)   p.push({ key: 'Company',  val: row.entitycompanyname   });
    if (row.entityposition)      p.push({ key: 'Position', val: row.entityposition      });
  }

  if (row.entityverifiedby)   p.push({ key: 'Verified By', val: row.entityverifiedby   });
  if (row.verifieddate)       p.push({ key: 'Verified On', val: row.verifieddate       });
  if (row.entitycustcomments) p.push({ key: 'Comments',    val: row.entitycustcomments });
  return p;
}

export default function OrderDetail({ onNavigate, orderId, profileStatus, onRefresh }) {
  const [order, setOrder] = useState(() => ({
    ref: orderId || '—', name: '—', status: profileStatus || '—',
    orderDate: '—', estCompletion: '—', credits: '—',
    email: '—', mobile: '—', empId: '—', dob: '—',
    orderType: '—', orderedBy: '—', internalNotes: '—', caseManager: '—',
    checks: [],
  }));
  const [communications, setCommunications] = useState([]);
  const [attachments, setAttachments]       = useState([]);
  const [loading, setLoading]               = useState(false);
  const [downloading, setDownloading]       = useState(new Set());
  const [activeTab, setActiveTab]           = useState('details');
  const [expandedChecks, setExpandedChecks] = useState({ 0: true });
  const [confirmModal, setConfirmModal]     = useState(null);
  const [actionDone, setActionDone]         = useState({});
  const [actionLoading, setActionLoading]   = useState(false);
  const [additionalCharges, setAdditionalCharges] = useState([]);
  const [expandedRemarks, setExpandedRemarks]     = useState({});
  const [commModal, setCommModal]               = useState(null); // { caseNumber, caseType }
  const [commRemark, setCommRemark]             = useState('');
  const [commFiles, setCommFiles]               = useState([]);
  const [commSubmitting, setCommSubmitting]     = useState(false);
  const [commDragOver, setCommDragOver]         = useState(false);
  const { addToast, walletBalance, refreshWalletBalance } = useApp();

  // ── Add Check ──────────────────────────────────────────
  const [addCheckOpen, setAddCheckOpen]         = useState(false);
  const [otherChecks, setOtherChecks]           = useState(null); // null = not loaded yet
  const [alacarteChecks, setAlacarteChecks]     = useState(null);
  const [checksLoading, setChecksLoading]       = useState(false);
  const [addQtys, setAddQtys]                   = useState([]);
  const [addFiles, setAddFiles]                 = useState([]);
  const [addDragOver, setAddDragOver]           = useState(false);
  const [addConfirmOpen, setAddConfirmOpen]     = useState(false);
  const [addSubmitting, setAddSubmitting]       = useState(false);

  function openCommModal(caseNumber, caseType) {
    setCommModal({ caseNumber, caseType });
    setCommRemark('');
    setCommFiles([]);
  }

  async function handleAddComm() {
    if (!commRemark.trim() && commFiles.length === 0) {
      addToast('Please enter a remark or attach a file.', 'error');
      return;
    }
    setCommSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('CASE_NUMBER', commModal.caseNumber);
      fd.append('remarks', commRemark.trim() || (commFiles.length > 0 ? 'File uploaded' : ''));
      commFiles.forEach(f => fd.append('file', f));
      const res = await ordersApi.addCommunication(fd);
      addToast(res?.message || 'Communication added successfully.', 'success');
      setCommModal(null);
    } catch (e) {
      addToast(e.message || 'Failed to submit. Please try again.', 'error');
    } finally {
      setCommSubmitting(false);
    }
  }

  async function handleDownload(attId, casecode, casetype, fallbackName) {
    if (downloading.has(attId)) return;
    setDownloading(prev => new Set(prev).add(attId));
    try {
      const result = await ordersApi.downloadAttachment(attId, casecode, casetype);
      if (!result) { addToast('Something went wrong. Please try again later.', 'error'); return; }
      const url = URL.createObjectURL(result.blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = result.filename || fallbackName || `attachment_${attId}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      addToast('Something went wrong. Please try again later.', 'error');
    } finally {
      setDownloading(prev => { const s = new Set(prev); s.delete(attId); return s; });
    }
  }

  function loadDetails() {
    if (!orderId) return;
    setLoading(true);
    ordersApi.getCandidateDetailsByID(orderId, profileStatus)
      .then(res => {
        const rows  = res?.data?.casedetails  || [];
        const comms = res?.data?.comunication || [];  // API typo: one 'm'
        const atts    = res?.data?.attchment        || [];  // API typo: missing 'a'
        const charges = res?.data?.additionalcharges || [];
        const apiStatus = res?.data?.table1?.[0]?.status;
        setCommunications(comms);
        setAttachments(atts);
        setAdditionalCharges(charges);
        if (rows.length > 0) {
          const r = rows[0];
          setOrder({
            ref:          r.profile_number         || orderId        || '—',
            name:         r.profilename                              || '—',
            status:       apiStatus || profileStatus || r.profstatus || '—',
            orderDate:    r.report_initiation_date                   || '—',
            estCompletion:r.report_completion_date                   || '—',
            credits:      r.hold_amount || r.credits                 || '—',
            creditsType:  r.hold_amount ? 'Hold' : (r.credits ? 'Debit' : ''),
            email:        r.email_address || r.email                || '—',
            mobile:       r.mobile        || r.mobile_number        || '—',
            empId:        r.employment_number                        || '—',
            dob:          r.dateofbirth                              || '—',
            orderType:     r.candidate_invited_on ? 'Invite Candidate' : 'Direct Upload',
            orderedBy:     r.ordered_by     || r.ORDERED_BY     || '—',
            internalNotes: r.internal_notes || r.INTERNAL_NOTES || '—',
            caseManager:   r.case_manager   || r.CASE_MANAGER   || '—',
            checks: rows.map(row => ({
              ct:         (row.case_type || '').toUpperCase(),
              title:      caseTitle(row),
              subType:    (row.case_type || '').toLowerCase() === 'education'  ? (row.entitycollege   || '') :
                          (row.case_type || '').toLowerCase() === 'employment' ? (row.entityemployer || '') :
                          (row.stype || ''),
              status:     row.entitystatus || row.entity_status || row.ENTITYSTATUS || row.ENTITY_STATUS,
              entity_status: row.entity_status || row.ENTITY_STATUS,
              caseNumber: row.case_number                            || '',
              details:    caseDetails(row),
              insuff:     (row.insuff_remarks || '').replace(/\r/g, '\n').trim(),
            })),
          });
          setExpandedChecks({ 0: true });
        }
      })
      .catch(e => addToast(e.message || 'Failed to load candidate details', 'error'))
      .finally(() => setLoading(false));
  }

  useEffect(() => { loadDetails(); }, [orderId, profileStatus]);

  function toggleCheck(i) { setExpandedChecks(e => ({ ...e, [i]: !e[i] })); }

  // Lazily loads the alacarte check list + pricing (same source as New Order) the first time the panel opens.
  function toggleAddCheckPanel() {
    setAddCheckOpen(open => {
      const next = !open;
      if (next && otherChecks === null && !checksLoading) {
        setChecksLoading(true);
        ordersApi.orderCandidate()
          .then(res => {
            if (res?.alacartejson) {
              try { setAlacarteChecks(JSON.parse(res.alacartejson)); }
              catch { setAlacarteChecks([]); }
            } else {
              setAlacarteChecks([]);
            }
            setOtherChecks(res?.otherchecks || []);
          })
          .catch(() => { addToast('Failed to load checks list', 'error'); setOtherChecks([]); setAlacarteChecks([]); })
          .finally(() => setChecksLoading(false));
      }
      return next;
    });
  }

  // [name, price] tuples aligned with otherChecks — price comes from the alacarte price list.
  const addCheckList = useMemo(() => {
    const priceMap = {};
    (alacarteChecks || []).forEach(c => {
      if (c.Text) priceMap[c.Text.toUpperCase()] = parseInt(c.Price, 10) || 0;
    });
    return (otherChecks || []).map(c => ({
      name:  c.check_name || c.CHECK_NAME || '',
      price: priceMap[(c.check_name || c.CHECK_NAME || '').toUpperCase()] || 0,
    }));
  }, [otherChecks, alacarteChecks]);

  useEffect(() => {
    setAddQtys(new Array(addCheckList.length).fill(0));
  }, [addCheckList.length]);

  function adjAddQty(i, delta) {
    setAddQtys(prev => {
      const next = [...prev];
      next[i] = Math.max(0, (next[i] || 0) + delta);
      return next;
    });
  }

  // Same validation as New Order → Direct Order: extension allow-list, 100MB cap, dedup by name+size.
  function addDocFiles(fileList) {
    const picked = Array.from(fileList);
    const valid = [];
    picked.forEach(f => {
      const ext = '.' + f.name.split('.').pop().toLowerCase();
      if (!ALLOWED_DOC_EXTS.includes(ext)) { addToast(`${f.name}: File type not supported.`, 'error'); return; }
      if (f.size > MAX_DOC_SIZE) { addToast(`${f.name} exceeds the 100 MB size limit.`, 'error'); return; }
      valid.push(f);
    });
    setAddFiles(prev => {
      const existing = new Set(prev.map(f => f.name + f.size));
      return [...prev, ...valid.filter(f => !existing.has(f.name + f.size))];
    });
  }

  function handleAddFileChange(e) {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    addDocFiles(picked);
  }

  const addCheckTotal = addCheckList.reduce((acc, c, i) => acc + (addQtys[i] || 0) * c.price, 0);
  const addCheckCount = addQtys.reduce((acc, q) => acc + (q > 0 ? 1 : 0), 0);
  const addCheckAfterBal = walletBalance - addCheckTotal;
  const addCheckInsufficient = addCheckTotal > 0 && addCheckAfterBal < 0;

  async function submitAddChecks() {
    setAddSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('profileid', String(orderId));
      (otherChecks || []).forEach((c, i) => {
        fd.append(`otherChecks[${i}].CHECK_NAME`, c.check_name ?? c.CHECK_NAME ?? '');
        fd.append(`otherChecks[${i}].CHECK_ID`, c.check_id ?? c.CHECK_ID ?? '');
        fd.append(`otherChecks[${i}].IS_MASTER_TYPE`, c.is_master_type ?? c.IS_MASTER_TYPE ?? '');
        fd.append(`otherChecks[${i}].MAIN_CHECKTYPE`, c.main_checktype ?? c.MAIN_CHECKTYPE ?? '');
        fd.append(`otherChecks[${i}].Other_Count`, addQtys[i] ?? 0);
      });
      addFiles.forEach(file => fd.append('BulkFiles', file));
      const res = await ordersApi.addChecksToProfile(fd);
      addToast(res.message || 'Checks added successfully.', 'success');
      setAddQtys(new Array(addCheckList.length).fill(0));
      setAddFiles([]);
      setAddConfirmOpen(false);
      setAddCheckOpen(false);
      refreshWalletBalance();
      loadDetails();
      if (onRefresh) onRefresh();
    } catch (e) {
      addToast(e.message || 'Failed to add checks. Please try again.', 'error');
    } finally {
      setAddSubmitting(false);
    }
  }

  function chargePrice(c) {
    return c.charges_type === 'Service Charges'
      ? { amount: c.price,        currency: c.currency    }
      : { amount: c.external_fee, currency: c.ex_currency };
  }
  function fmtAmt(amount, currency) {
    const sym = currency === 'INR' ? '₹' : (currency || '');
    return `${sym}${Number(amount || 0).toLocaleString('en-IN')}`;
  }
  const pendingCharges = additionalCharges.filter(c => c.status === 'Requested');
  const modalCharge    = confirmModal ? chargePrice(confirmModal.charge) : null;
  const modalFmt       = modalCharge  ? fmtAmt(modalCharge.amount, modalCharge.currency) : '';
  const modalExtFee    = (confirmModal?.charge?.charges_type === 'Service Charges' && Number(confirmModal?.charge?.external_fee) > 0)
    ? Number(confirmModal.charge.external_fee)
    : 0;
  const modalTotalAmt  = (Number(modalCharge?.amount) || 0) + modalExtFee;
  const modalTotalFmt  = modalCharge ? fmtAmt(modalTotalAmt, modalCharge.currency) : '';
  const afterBal       = modalCharge
    ? walletBalance - modalTotalAmt
    : walletBalance;

  async function handleChargeAction(type, charge) {
    setActionLoading(true);
    // snapshot before API call — walletBalance will change after refreshWalletBalance()
    const { amount: snapAmt } = chargePrice(charge);
    const snapExt  = charge.charges_type === 'Service Charges' && Number(charge.external_fee) > 0
      ? Number(charge.external_fee) : 0;
    const snapAfterBal = walletBalance - (Number(snapAmt) || 0) - snapExt;
    try {
      const approvalType = type === 'approve' ? 'Approved' : 'Rejected';
      await ordersApi.requestChargesApprovalOrReject(
        approvalType,
        charge.charges_type   || '',
        charge.case_number    || '',
        charge.profile_number || order.ref || '',
        order.ref  || '',
        order.name || '',
      );
      addToast(
        type === 'approve' ? 'Charge approved successfully' : 'Charge rejected',
        type === 'approve' ? 'default' : 'error'
      );
      setActionDone(prev => ({ ...prev, [charge.charges_id]: { status: type === 'approve' ? 'approved' : 'rejected', afterBal: snapAfterBal } }));
      refreshWalletBalance();
      if (onRefresh) onRefresh();
    } catch (e) {
      addToast(e.message || 'Action failed', 'error');
    } finally {
      setActionLoading(false);
      setConfirmModal(null);
    }
  }

  return (
    <>
    <div className="dover" onClick={() => onNavigate('orders')}>
      <div className="drawer" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="dhead">
          <div>
            <div className="dtitle">
              {order.ref}{order.name !== '—' ? ` · ${order.name}` : ''}
            </div>
            <div className="flex g8 mt8">
              {order.status !== '—' && <Chip label={order.status} />}
            </div>
          </div>
          <button className="dclose" onClick={() => onNavigate('orders')}>✕</button>
        </div>

        <div className="dbody">

          {/* Mini cards */}
          <div className="mcards">
            <div className="mcard"><div className="mval">{order.orderDate}</div><div className="mlbl">Order Date</div></div>
            <div className="mcard"><div className="mval">{order.estCompletion}</div><div className="mlbl">Est. Completion</div></div>
            <div className="mcard"><div className="mval" style={{ color: 'var(--or-dk)' }}>{`₹${order.credits}`}</div><div className="mlbl">Credits <Chip label={order.creditsType} /></div></div>
          </div>

          {/* Quick info */}
          <div className="igrid">
            <div className="iitem"><div className="ikey">Email</div><div className="ival">{order.email}</div></div>
            <div className="iitem"><div className="ikey">Mobile</div><div className="ival">{order.mobile}</div></div>
            <div className="iitem"><div className="ikey">Emp. ID</div><div className="ival">{order.empId}</div></div>
            <div className="iitem"><div className="ikey">Date of Birth</div><div className="ival">{order.dob}</div></div>
          </div>
          {/* Additional Charge Approval Cards */}
          {pendingCharges.map(charge => {
            const id = charge.charges_id;
            const done = actionDone[id];
            const { amount, currency } = chargePrice(charge);
            const fmtCharge    = fmtAmt(amount, currency);
            const extFeeAmt    = charge.charges_type === 'Service Charges' && Number(charge.external_fee) > 0
              ? Number(charge.external_fee) : 0;
            const totalAmt     = (Number(amount) || 0) + extFeeAmt;
            const fmtTotal     = fmtAmt(totalAmt, currency);
            const afterBalance = walletBalance - totalAmt;
            const remarks = charge.remarks || 'An additional charge must be approved before this check can proceed.';
            const isLong = remarks.length > 150;
            const isExpanded = !!expandedRemarks[id];

            if (done) {
              const isApproved = done.status === 'approved';
              return (
                <div key={id} style={{
                  background: isApproved ? 'rgba(34,197,94,.07)' : 'rgba(229,62,62,.06)',
                  border: `1.5px solid ${isApproved ? 'rgba(34,197,94,.4)' : 'rgba(229,62,62,.3)'}`,
                  borderRadius: 'var(--r)', padding: '14px 16px', margin: '14px 0',
                  display: 'flex', alignItems: 'flex-start', gap: 12,
                }}>
                  {isApproved ? (
                    <svg width="36" height="36" viewBox="0 0 36 36" style={{ flexShrink: 0 }}>
                      <rect x="2" y="2" width="32" height="32" rx="8" fill="#22C55E"/>
                      <path d="M10 18 L15.5 23.5 L26 12" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                    </svg>
                  ) : (
                    <svg width="36" height="36" viewBox="0 0 36 36" style={{ flexShrink: 0 }}>
                      <circle cx="18" cy="18" r="15" fill="rgba(229,62,62,.1)" stroke="#E53E3E" strokeWidth="2.5"/>
                      <line x1="9" y1="27" x2="27" y2="9" stroke="#E53E3E" strokeWidth="2.5" strokeLinecap="round"/>
                    </svg>
                  )}
                  <div>
                    <div style={{ fontSize: 13.5, fontWeight: 700, color: isApproved ? '#15803D' : 'var(--rd)', marginBottom: 4 }}>
                      {isApproved ? 'Additional Charge Approved' : 'Charge Rejected — Check Cancelled'}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--g600)', lineHeight: 1.55 }}>
                      {isApproved ? (
                        <>{fmtTotal} deducted &middot; Case {charge.case_number || ''} resumed &middot; New balance: <strong style={{ color: 'var(--nv)' }}>₹{(done.afterBal ?? afterBalance).toLocaleString('en-IN')}</strong></>
                      ) : (
                        <>Case {charge.case_number || ''} has been cancelled. No credits deducted. Wallet unchanged at <strong style={{ color: 'var(--nv)' }}>₹{walletBalance.toLocaleString('en-IN')}</strong>.</>
                      )}
                    </div>
                  </div>
                </div>
              );
            }

            return (
              <div key={id} style={{ background: 'linear-gradient(135deg,#fff5f5,#fff8f8)', border: '1.5px solid rgba(229,62,62,.3)', borderRadius: 'var(--r)', padding: 14, margin: '14px 0' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 10 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--rd)' }}>🔴 Additional Charge Approval Required</div>
                  <span style={{ fontSize: 10, fontWeight: 700, background: 'rgba(229,62,62,.1)', color: 'var(--rd)', padding: '3px 9px', borderRadius: 20, whiteSpace: 'nowrap', flexShrink: 0 }}>Action Required</span>
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--g600)', marginBottom: 8, lineHeight: 1.5 }}>
                  <div style={isExpanded ? {} : { overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>
                    {remarks}
                  </div>
                  {isLong && (
                    <button
                      onClick={() => setExpandedRemarks(r => ({ ...r, [id]: !r[id] }))}
                      style={{ background: 'none', border: 'none', color: 'var(--or)', fontFamily: 'var(--fn)', fontSize: 11, fontWeight: 600, cursor: 'pointer', padding: '2px 0', marginTop: 2 }}
                    >
                      {isExpanded ? 'Show less ▲' : 'Show more ▼'}
                    </button>
                  )}
                </div>
                <div style={{ background: 'rgba(26,31,54,.04)', borderRadius: 8, padding: '11px 14px', margin: '10px 0', border: '1px solid var(--g200)' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px 12px' }}>
                    <span style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--g400)', textTransform: 'uppercase', letterSpacing: '.4px' }}>Check</span>
                    <span style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--g400)', textTransform: 'uppercase', letterSpacing: '.4px' }}>Case No.</span>
                    <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--nv)' }}>{ctIcon(charge.case_type)} {(charge.case_type || '').replace(/_/g, ' ')}</span>
                    <span style={{ fontSize: 11.5, fontFamily: 'monospace', color: 'var(--g600)', fontWeight: 600 }}>{charge.case_number || '—'}</span>
                  </div>
                  <div style={{ borderTop: '1px solid var(--g200)', marginTop: 8, paddingTop: 8, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px 12px' }}>
                    <span style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--g400)', textTransform: 'uppercase', letterSpacing: '.4px' }}>Charge Type</span>
                    <span style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--g400)', textTransform: 'uppercase', letterSpacing: '.4px' }}>Amount</span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--nv)' }}>{charge.charges_type}</span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--rd)' }}>{fmtCharge}</span>
                  </div>
                  {charge.charges_type === 'Service Charges' && Number(charge.external_fee) > 0 && (
                    <div style={{ borderTop: '1px solid var(--g100)', marginTop: 6, paddingTop: 6, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px 12px' }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--g500)' }}>External Fee</span>
                      <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--rd)' }}>{fmtAmt(charge.external_fee, charge.ex_currency)}</span>
                    </div>
                  )}
                </div>
                <div style={{ fontSize: 11, color: 'var(--g400)', marginBottom: 12, lineHeight: 1.6 }}>
                  ⚠️ Approving will immediately deduct <strong style={{ color: 'var(--rd)' }}>{fmtTotal}</strong> from your wallet (current balance: <strong style={{ color: 'var(--nv)' }}>₹{walletBalance.toLocaleString('en-IN')}</strong>). Rejecting will cancel this check.
                </div>
                <div className="flex g8">
                  <button className="btn bsm" style={{ background: 'var(--gr)', color: '#fff' }} onClick={() => setConfirmModal({ type: 'approve', charge })}>✅ Approve &amp; Deduct</button>
                  <button className="btn bsm" style={{ background: 'var(--rd)', color: '#fff' }} onClick={() => setConfirmModal({ type: 'reject', charge })}>✕ Reject</button>
                </div>
              </div>
            );
          })}

          {/* Confirm Modal */}
          {confirmModal && (
            <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(26,31,54,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(3px)' }}
              onClick={() => setConfirmModal(null)}>
              <div style={{ background: 'var(--wh)', borderRadius: 16, boxShadow: '0 16px 48px rgba(26,31,54,.22)', width: '100%', maxWidth: 420, margin: 20, overflow: 'hidden' }}
                onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div style={{ padding: '18px 22px 14px', borderBottom: '1px solid var(--g100)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 38, height: 38, borderRadius: 10, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, background: confirmModal.type === 'approve' ? 'rgba(13,179,107,.12)' : 'rgba(229,62,62,.1)' }}>
                      {confirmModal.type === 'approve' ? '✅' : '🚫'}
                    </div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--nv)' }}>{confirmModal.type === 'approve' ? 'Confirm Approval' : 'Confirm Rejection'}</div>
                      <div style={{ fontSize: 11, color: 'var(--g400)', marginTop: 2 }}>{order.ref}{order.name !== '—' ? ` · ${order.name}` : ''}</div>
                    </div>
                  </div>
                  <button onClick={() => setConfirmModal(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: 'var(--g400)', padding: 2, lineHeight: 1 }}>✕</button>
                </div>

                {/* Unified detail strip */}
                <div style={{ margin: '14px 22px', background: 'var(--g50)', border: '1px solid var(--g200)', borderRadius: 9, padding: '0 14px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--g100)' }}>
                    <span style={{ fontSize: 11, color: 'var(--g400)', fontWeight: 600 }}>Check type</span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--nv)' }}>{ctIcon(confirmModal.charge.case_type)} {(confirmModal.charge.case_type || '').replace(/_/g, ' ')}</span>
                  </div>
                  {confirmModal.charge.case_number && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--g100)' }}>
                      <span style={{ fontSize: 11, color: 'var(--g400)', fontWeight: 600 }}>Case No.</span>
                      <span style={{ fontSize: 11.5, fontFamily: 'monospace', color: 'var(--g600)', background: 'var(--wh)', padding: '2px 8px', borderRadius: 4, border: '1px solid var(--g200)' }}>{confirmModal.charge.case_number}</span>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--g100)' }}>
                    <span style={{ fontSize: 11, color: 'var(--g400)', fontWeight: 600 }}>{confirmModal.charge.charges_type || 'Additional Charge'}</span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: confirmModal.type === 'approve' ? '#0A9B5F' : 'var(--g600)' }}>
                      {confirmModal.type === 'approve' ? `+ ${modalFmt}` : modalFmt}
                    </span>
                  </div>
                  {confirmModal.charge.charges_type === 'Service Charges' && Number(confirmModal.charge.external_fee) > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--g100)' }}>
                      <span style={{ fontSize: 11, color: 'var(--g400)', fontWeight: 600 }}>External Fee</span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: confirmModal.type === 'approve' ? '#0A9B5F' : 'var(--g400)' }}>
                        {confirmModal.type === 'approve' ? '+ ' : ''}{fmtAmt(confirmModal.charge.external_fee, confirmModal.charge.ex_currency)}
                      </span>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0' }}>
                    <span style={{ fontSize: 11, color: 'var(--g400)', fontWeight: 600 }}>Wallet balance</span>
                    {confirmModal.type === 'approve' ? (
                      <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--g600)' }}>
                        ₹{walletBalance.toLocaleString('en-IN')}
                        <span style={{ margin: '0 5px', color: 'var(--g300)' }}>→</span>
                        <span style={{ color: 'var(--rd)', fontWeight: 700 }}>₹{afterBal.toLocaleString('en-IN')}</span>
                      </span>
                    ) : (
                      <span style={{ fontSize: 12, fontWeight: 600, color: '#0A9B5F' }}>No deduction</span>
                    )}
                  </div>
                </div>

                {/* Body */}
                <div style={{ padding: '0 22px 16px', fontSize: 12.5, color: 'var(--g600)', lineHeight: 1.65 }}>
                  {confirmModal.type === 'approve' ? (
                    <>
                      <p style={{ margin: '0 0 10px' }}>⚠️ You are about to <strong style={{ color: 'var(--nv)' }}>approve</strong> the additional charge of <strong style={{ color: 'var(--nv)' }}>{modalTotalFmt}</strong> for this check. This amount will be immediately deducted from your wallet.</p>
                      <p style={{ margin: 0 }}>This action <strong style={{ color: 'var(--nv)' }}>cannot be undone</strong>. The check will resume once approved.</p>
                    </>
                  ) : (
                    <span>⚠️ You are about to <strong style={{ color: 'var(--nv)' }}>reject</strong> this additional charge. The check will be <strong style={{ color: 'var(--rd)' }}>cancelled</strong> and cannot be restarted without a new order. No credits will be deducted. This action <strong style={{ color: 'var(--nv)' }}>cannot be undone</strong>.</span>
                  )}
                </div>

                {/* Actions */}
                <div style={{ padding: '14px 22px 18px', borderTop: '1px solid var(--g100)', display: 'flex', gap: 9, justifyContent: 'flex-end' }}>
                  <button onClick={() => setConfirmModal(null)} style={{ padding: '9px 18px', borderRadius: 8, border: '1.5px solid var(--g200)', background: 'var(--wh)', fontFamily: 'var(--fn)', fontSize: 12.5, fontWeight: 600, color: 'var(--g600)', cursor: 'pointer' }}>Cancel</button>
                  <button disabled={actionLoading} onClick={() => handleChargeAction(confirmModal.type, confirmModal.charge)}
                    style={{ padding: '9px 20px', borderRadius: 8, border: 'none', fontFamily: 'var(--fn)', fontSize: 12.5, fontWeight: 700, cursor: actionLoading ? 'default' : 'pointer', background: confirmModal.type === 'approve' ? '#0DB36B' : '#E53E3E', color: '#fff', opacity: actionLoading ? 0.7 : 1 }}>
                    {actionLoading ? <span className="spinner" style={{ width: 14, height: 14, borderColor: 'rgba(255,255,255,0.3)', borderTopColor: '#fff' }} /> : confirmModal.type === 'approve' ? `✅ Yes, Approve & Deduct ${modalTotalFmt}` : '🚫 Yes, Reject This Check'}
                  </button>
                </div>
              </div>
            </div>
          )}
          {/* Add Communication Modal */}
          {commModal && (
            <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(26,31,54,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(3px)' }}
              onClick={() => setCommModal(null)}>
              <div style={{ background: 'var(--wh)', borderRadius: 14, boxShadow: '0 16px 48px rgba(26,31,54,.22)', width: '100%', maxWidth: 440, margin: 20, display: 'flex', flexDirection: 'column', maxHeight: 'calc(100vh - 40px)', overflow: 'hidden' }}
                onClick={e => e.stopPropagation()}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid var(--g100)', flexShrink: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--nv)' }}>Add Communication</div>
                  <button onClick={() => setCommModal(null)} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: 'var(--g400)', lineHeight: 1 }}>✕</button>
                </div>
                <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 12, overflowY: 'auto' }}>
                  {commModal.caseNumber && (
                    <div style={{ fontSize: 12, color: 'var(--g600)' }}>
                      Case No.: <strong style={{ color: 'var(--nv)', fontFamily: 'monospace' }}>{commModal.caseNumber}</strong>
                    </div>
                  )}
                  <div className="fg">
                    <label className="flbl">Remarks</label>
                    <textarea
                      className="inp"
                      rows={3}
                      placeholder="Enter your remarks here…"
                      value={commRemark}
                      onChange={e => setCommRemark(e.target.value)}
                      style={{ resize: 'vertical', minHeight: 72 }}
                    />
                  </div>
                  <div className="fg">
                    <label className="flbl">Attach File (optional)</label>
                    <label
                      className="upzone"
                      style={{ display: 'block', padding: '16px', ...(commDragOver ? { borderColor: 'var(--or)', background: 'var(--or-lt)', boxShadow: '0 0 0 2px rgba(245,166,35,.2)' } : {}) }}
                      onDragOver={e => { e.preventDefault(); setCommDragOver(true); }}
                      onDragLeave={() => setCommDragOver(false)}
                      onDrop={e => { e.preventDefault(); setCommDragOver(false); setCommFiles(prev => [...prev, ...Array.from(e.dataTransfer.files)]); }}
                    >
                      <input type="file" multiple style={{ display: 'none' }} onChange={e => setCommFiles(prev => [...prev, ...Array.from(e.target.files)])} />
                      <div style={{ fontSize: '24px', marginBottom: '6px' }}>📂</div>
                      <div style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--nv)' }}><strong>Click to upload</strong> or drag &amp; drop</div>
                      <div style={{ fontSize: '11px', color: 'var(--g400)', marginTop: '3px' }}>All file types supported · Multiple files allowed</div>
                    </label>
                    {commFiles.map((f, i) => (
                      <div key={i} className="upfile" style={{ marginTop: '8px' }}>
                        <div className="ufinfo">
                          <span style={{ fontSize: '18px' }}>📎</span>
                          <div>
                            <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--nv)' }}>{f.name}</div>
                            <div style={{ fontSize: '10.5px', color: 'var(--g400)' }}>{Math.round(f.size / 1024)} KB · Attached</div>
                          </div>
                        </div>
                        <span style={{ fontSize: '13px', color: 'var(--g400)', cursor: 'pointer' }} onClick={() => setCommFiles(prev => prev.filter((_, j) => j !== i))}>✕</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div style={{ padding: '12px 20px 16px', borderTop: '1px solid var(--g100)', display: 'flex', gap: 8, justifyContent: 'flex-end', flexShrink: 0 }}>
                  <button onClick={() => setCommModal(null)} style={{ padding: '8px 16px', borderRadius: 8, border: '1.5px solid var(--g200)', background: 'var(--wh)', fontFamily: 'var(--fn)', fontSize: 12.5, fontWeight: 600, color: 'var(--g600)', cursor: 'pointer' }}>Cancel</button>
                  <button onClick={handleAddComm} disabled={commSubmitting} style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: 'var(--or)', fontFamily: 'var(--fn)', fontSize: 12.5, fontWeight: 700, color: 'var(--nv)', cursor: commSubmitting ? 'default' : 'pointer', opacity: commSubmitting ? 0.7 : 1 }}>
                    {commSubmitting ? <><span className="spinner" style={{ width: 13, height: 13, borderColor: 'rgba(20,30,60,0.25)', borderTopColor: 'var(--nv)' }} /> Submitting…</> : 'Submit'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Check-wise status */}
          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '16px 0', color: 'var(--g400)', fontSize: 12.5 }}>
              <span className="spinner" style={{ width: 16, height: 16 }} /> Loading…
            </div>
          ) : order.checks.length > 0 && (
            <>
              <div className="hr" />
              <div style={{ fontSize: '10.5px', fontWeight: 700, color: 'var(--g400)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: '9px' }}>Check-wise Status</div>
              {order.checks.map((check, i) => (
                <div key={i} className="chksec">
                  <div className="chkhd" onClick={() => toggleCheck(i)}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                      <span className="chktype">{ctIcon(check.ct)}</span>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <div className="chkhdl">
                          <span className="chktype">{check.title}</span>
                          <Chip label={check.status} />
                        </div>
                        {check.subType && (
                          <span style={{ fontSize: 11, color: 'var(--g400)' }}>{check.subType}</span>
                        )}
                      </div>
                    </div>
                    <span>{expandedChecks[i] ? '▼' : '▶'}</span>
                  </div>
                  {expandedChecks[i] && (
                    <div className="chkbody">
                      {check.caseNumber && (
                        <div className="chkrow">
                          <div className="chkkey">Case No.</div>
                          <div className="chkval" style={{ fontFamily: 'monospace', fontSize: 11 }}>{check.caseNumber}</div>
                        </div>
                      )}
                      {check.details.map((d, j) => (
                        <div key={j} className="chkrow">
                          <div className="chkkey">{d.key}</div>
                          <div className="chkval">{d.val}</div>
                        </div>
                      ))}
                      {check.insuff && (
                        <div style={{ background: 'rgba(220,38,38,.06)', border: '1px solid rgba(220,38,38,.18)', borderRadius: 6, padding: '8px 10px', marginTop: 6 }}>
                          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--rd)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 4 }}>Insuff. Remarks</div>
                          <div style={{ fontSize: 12, color: 'var(--rd)', whiteSpace: 'pre-line', lineHeight: 1.6 }}>{check.insuff}</div>
                        </div>
                      )}
                      {check.entity_status?.toUpperCase() === 'ADDITIONAL_DOCS' && (
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                          <button
                            className="btn bpri bsm"
                            onClick={() => openCommModal(check.caseNumber, check.ct)}
                          >
                            + Add Communication
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </>
          )}

          {!loading && !['Invited - Not Started', 'Invited - Partial Complete'].includes(order.status) && (
            <>
              <div className="hr" />
              <div className="chksec">
                <div className="chkhd" onClick={toggleAddCheckPanel}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span className="chktype">➕</span>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <span className="chktype">Add More Checks</span>
                      <span style={{ fontSize: 11, color: 'var(--g400)' }}>Click to expand &amp; select additional checks</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {addCheckCount > 0 && <Chip label={`${addCheckCount} selected`} />}
                    <span>{addCheckOpen ? '▼' : '▶'}</span>
                  </div>
                </div>
                {addCheckOpen && (
                  <div className="chkbody">
                    {checksLoading ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 0', color: 'var(--g400)', fontSize: 12.5 }}>
                        <span className="spinner" style={{ width: 14, height: 14 }} /> Loading checks…
                      </div>
                    ) : addCheckList.length === 0 ? (
                      <div style={{ color: 'var(--g400)', fontSize: 12, textAlign: 'center', padding: '10px 0' }}>No additional checks available for your account.</div>
                    ) : (
                      <>
                        <div className="checks-wrap" style={{ marginBottom: 10 }}>
                          <table>
                            <thead>
                              <tr><th>Check Type</th><th>Unit Price</th><th>Qty</th><th>Amount</th></tr>
                            </thead>
                            <tbody>
                              {addCheckList.map((c, i) => {
                                const qty = addQtys[i] || 0;
                                return (
                                  <tr key={c.name} className={qty > 0 ? 'has-qty' : ''}>
                                    <td style={{ padding: '9px 13px' }}><div style={{ fontSize: 12, fontWeight: 600, color: 'var(--nv)' }}>{c.name}</div></td>
                                    <td style={{ padding: '9px 13px', textAlign: 'right', fontSize: 12, fontWeight: 700, color: 'var(--or-dk)', whiteSpace: 'nowrap' }}>
                                      {c.price === 0 ? <span style={{ color: 'var(--g300)', fontSize: 11 }}>Std.</span> : `₹${c.price}`}
                                    </td>
                                    <td style={{ padding: '9px 13px', textAlign: 'center' }}>
                                      <div className="qty-stepper">
                                        <button className="qty-btn qty-btn-minus" onClick={() => adjAddQty(i, -1)}>−</button>
                                        <span className="qty-val">{qty}</span>
                                        <button className="qty-btn qty-btn-plus" onClick={() => adjAddQty(i, 1)}>+</button>
                                      </div>
                                    </td>
                                    <td style={{ padding: '9px 13px', textAlign: 'right', fontSize: 12, fontWeight: 700, color: qty > 0 ? 'var(--or-dk)' : 'var(--g300)' }}>
                                      {qty > 0 ? `₹${(qty * c.price).toLocaleString('en-IN')}` : '—'}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>

                        <div className="card mb12" style={{ padding: 12 }}>
                          <div className="chd" style={{ marginBottom: 8 }}><div className="ctitle">Upload Documents</div><div className="csub">Optional supporting documents</div></div>
                          <label
                            className="upzone"
                            style={{ display: 'block', padding: '10px', ...(addDragOver ? { borderColor: 'var(--or)', background: 'var(--or-lt)', boxShadow: '0 0 0 2px rgba(245,166,35,.2)' } : {}) }}
                            onDragOver={e => { e.preventDefault(); setAddDragOver(true); }}
                            onDragLeave={() => setAddDragOver(false)}
                            onDrop={e => { e.preventDefault(); setAddDragOver(false); addDocFiles(e.dataTransfer.files); }}
                          >
                            <input type="file" multiple accept=".xlsx,.xls,.doc,.docx,.pdf,.zip,.rar,.png,.jpg,.jpeg" onChange={handleAddFileChange} style={{ display: 'none' }} />
                            <div style={{ fontSize: 28, marginBottom: 8 }}>📂</div>
                            <div style={{ fontSize: 13, fontWeight: 600 }}><strong>Click to upload</strong> or drag &amp; drop documents</div>
                            <div style={{ fontSize: 11, color: 'var(--g400)', marginTop: 6 }}>
                              .xlsx &nbsp;·&nbsp; .xls &nbsp;·&nbsp; .doc &nbsp;·&nbsp; .docx &nbsp;·&nbsp; .pdf &nbsp;·&nbsp; .zip &nbsp;·&nbsp; .rar &nbsp;·&nbsp; .png &nbsp;·&nbsp; .jpg &nbsp;·&nbsp; .jpeg &nbsp;·&nbsp; Upto 100 MB accepted
                            </div>
                          </label>
                          {addFiles.map((f, i) => (
                            <div key={i} className="upfile mt8">
                              <div className="ufinfo"><span style={{ fontSize: 16 }}>📄</span><div><div className="ufname">{f.name}</div><div className="ufsize">{(f.size / 1024).toFixed(0)} KB</div></div></div>
                              <span className="ufrm" onClick={() => setAddFiles(fs => fs.filter((_, j) => j !== i))}>✕</span>
                            </div>
                          ))}
                        </div>

                        <div className="amtbox mb12">
                          <div className="amtrow"><span className="aml">Available Balance</span><span className="amv" style={{ color: 'var(--gr)' }}>₹{walletBalance.toLocaleString('en-IN')}</span></div>
                          <div className="amtrow"><span className="aml">Selected Checks Cost</span><span className="amv" style={{ color: 'var(--rd)' }}>₹{addCheckTotal.toLocaleString('en-IN')}</span></div>
                          <div className="amtrow"><span className="amtl">Balance After Deduction</span><span className="amtv" style={{ color: addCheckAfterBal < 0 ? 'var(--rd)' : 'var(--nv)', fontSize: 15 }}>₹{addCheckAfterBal.toLocaleString('en-IN')}</span></div>
                        </div>

                        {addCheckInsufficient && (
                          <div className="alert a-rd mb12">
                            <div className="aico">🔴</div>
                            <div className="abody"><div className="atitle">Insufficient credits</div>Your wallet balance is insufficient for the selected checks. Please add credits before continuing.</div>
                          </div>
                        )}

                        <button
                          className="btn bpri bsm wf"
                          style={{ justifyContent: 'center', ...(addCheckTotal === 0 || addCheckInsufficient ? { opacity: 0.45, cursor: 'not-allowed' } : {}) }}
                          disabled={addCheckTotal === 0 || addCheckInsufficient}
                          onClick={() => setAddConfirmOpen(true)}
                        >
                          ➕ Add {addCheckCount > 0 ? `${addCheckCount} Check${addCheckCount > 1 ? 's' : ''} — ` : ''}Deduct ₹{addCheckTotal.toLocaleString('en-IN')}
                        </button>

                        {addCheckInsufficient && (
                          <button className="btn bpri wf mt8" style={{ justifyContent: 'center' }} onClick={() => onNavigate('wallet')}>
                            + Add Credits First →
                          </button>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            </>
          )}

          <div className="hr" />

          {/* Tabs */}
          <div className="tabbar mb16">
            <div className={`tab ${activeTab === 'details' ? 'on' : ''}`} onClick={() => setActiveTab('details')}>Details</div>
            <div className={`tab ${activeTab === 'comms' ? 'on' : ''}`} onClick={() => setActiveTab('comms')}>
              Communications{communications.length > 0 ? ` (${communications.length})` : ''}
            </div>
            <div className={`tab ${activeTab === 'attachments' ? 'on' : ''}`} onClick={() => setActiveTab('attachments')}>
              Attachments{attachments.length > 0 ? ` (${attachments.length})` : ''}
            </div>
          </div>

          {/* Details tab */}
          {activeTab === 'details' && (
            <div className="igrid">
              <div className="iitem"><div className="ikey">Order Type</div><div className="ival">{order.orderType}</div></div>
              <div className="iitem"><div className="ikey">Ordered By</div><div className="ival">{order.orderedBy}</div></div>
              <div className="iitem"><div className="ikey">Internal Notes</div><div className="ival">{order.internalNotes}</div></div>
              <div className="iitem"><div className="ikey">Case Manager</div><div className="ival">{order.caseManager}</div></div>
            </div>
          )}

          {/* Communications tab */}
          {activeTab === 'comms' && (
            communications.length === 0 ? (
              <div style={{ color: 'var(--g400)', fontSize: 12.5, textAlign: 'center', padding: '24px 0' }}>No communications yet</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: 'var(--g50)' }}>
                      {['Date', 'Remark', 'Type', 'Attachment'].map(h => (
                        <th key={h} style={{ padding: '6px 8px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: 'var(--g400)', textTransform: 'uppercase', letterSpacing: '.5px', borderBottom: '1px solid var(--g200)', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {communications.map((c, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid var(--g100)' }}>
                        <td style={{ padding: '7px 8px', color: 'var(--g400)', fontSize: 11, whiteSpace: 'nowrap', verticalAlign: 'top' }}>{c.creation_date || '—'}</td>
                        <td style={{ padding: '7px 8px', color: 'var(--nv)', verticalAlign: 'top', maxWidth: 160, wordBreak: 'break-word' }}>{c.description || '—'}</td>
                        <td style={{ padding: '7px 8px', color: 'var(--g400)', fontSize: 11, whiteSpace: 'nowrap', verticalAlign: 'top' }}>{c.communicationtype || c.communicated_by || '—'}</td>
                        <td style={{ padding: '7px 8px', verticalAlign: 'top' }}>
                          {c.attachment_id
                            ? <span
                                style={{ color: downloading.has(c.docid) ? 'var(--g300)' : 'var(--or)', cursor: downloading.has(c.docid) ? 'default' : 'pointer', fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                                onClick={() => handleDownload(c.docid, c.key_value, c.key_code, c.document)}
                              >📎 {downloading.has(c.docid) ? <span className="spinner" style={{ width: 10, height: 10 }} /> : 'Download'}</span>
                            : <span style={{ color: 'var(--g300)', fontSize: 11 }}>—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}

          {/* Attachments tab */}
          {activeTab === 'attachments' && (
            attachments.length === 0 ? (
              <div style={{ color: 'var(--g400)', fontSize: 12.5, textAlign: 'center', padding: '24px 0' }}>No attachments uploaded</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {attachments.map((a, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 12px', background: 'var(--g50)', borderRadius: 6, border: '1px solid var(--g100)' }}>
                    <div>
                      <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--nv)' }}>{a.attachment_type || a.attachment_name}</div>
                      <div style={{ fontSize: 10.5, color: 'var(--g400)', marginTop: 2 }}>{a.creation_date}</div>
                    </div>
                    <span
                      style={{ color: downloading.has(a.attachment_id) ? 'var(--g300)' : 'var(--or)', cursor: downloading.has(a.attachment_id) ? 'default' : 'pointer', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 5 }}
                      onClick={() => handleDownload(a.attachment_id, a.key_code, a.attachment_type, a.attachment_name)}
                    >
                      📎 {downloading.has(a.attachment_id) ? <><span className="spinner" style={{ width: 10, height: 10 }} /> Downloading…</> : 'Download'}
                    </span>
                  </div>
                ))}
              </div>
            )
          )}

        </div>
      </div>
    </div>

    {/* Confirm Modal — rendered outside .dover to avoid backdrop-filter stacking context */}
    {confirmModal && (
      <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(26,31,54,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(3px)' }}
        onClick={() => setConfirmModal(null)}>
        <div style={{ background: 'var(--wh)', borderRadius: 16, boxShadow: '0 16px 48px rgba(26,31,54,.22)', width: '100%', maxWidth: 420, margin: 20, overflow: 'hidden' }}
          onClick={e => e.stopPropagation()}>

          {/* Header */}
          <div style={{ padding: '18px 22px 14px', borderBottom: '1px solid var(--g100)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 38, height: 38, borderRadius: 10, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, background: confirmModal.type === 'approve' ? 'rgba(13,179,107,.12)' : 'rgba(229,62,62,.1)' }}>
                {confirmModal.type === 'approve' ? '✅' : '🚫'}
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--nv)' }}>{confirmModal.type === 'approve' ? 'Confirm Approval' : 'Confirm Rejection'}</div>
                <div style={{ fontSize: 11, color: 'var(--g400)', marginTop: 2 }}>{order.ref}{order.name !== '—' ? ` · ${order.name}` : ''}</div>
              </div>
            </div>
            <button onClick={() => setConfirmModal(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: 'var(--g400)', padding: 2, lineHeight: 1 }}>✕</button>
          </div>

          {/* Unified detail strip */}
          <div style={{ margin: '14px 22px', background: 'var(--g50)', border: '1px solid var(--g200)', borderRadius: 9, padding: '0 14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--g100)' }}>
              <span style={{ fontSize: 11, color: 'var(--g400)', fontWeight: 600 }}>Check type</span>
              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--nv)' }}>{ctIcon(confirmModal.charge.case_type)} {(confirmModal.charge.case_type || '').replace(/_/g, ' ')}</span>
            </div>
            {confirmModal.charge.case_number && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--g100)' }}>
                <span style={{ fontSize: 11, color: 'var(--g400)', fontWeight: 600 }}>Case No.</span>
                <span style={{ fontSize: 11.5, fontFamily: 'monospace', color: 'var(--g600)', background: 'var(--wh)', padding: '2px 8px', borderRadius: 4, border: '1px solid var(--g200)' }}>{confirmModal.charge.case_number}</span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--g100)' }}>
              <span style={{ fontSize: 11, color: 'var(--g400)', fontWeight: 600 }}>{confirmModal.charge.charges_type || 'Additional Charge'}</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: confirmModal.type === 'approve' ? '#0A9B5F' : 'var(--g600)' }}>
                {confirmModal.type === 'approve' ? `+ ${modalFmt}` : modalFmt}
              </span>
            </div>
            {confirmModal.charge.charges_type === 'Service Charges' && Number(confirmModal.charge.external_fee) > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--g100)' }}>
                <span style={{ fontSize: 11, color: 'var(--g400)', fontWeight: 600 }}>External Fee</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: confirmModal.type === 'approve' ? '#0A9B5F' : 'var(--g400)' }}>
                  {confirmModal.type === 'approve' ? '+ ' : ''}{fmtAmt(confirmModal.charge.external_fee, confirmModal.charge.ex_currency)}
                </span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0' }}>
              <span style={{ fontSize: 11, color: 'var(--g400)', fontWeight: 600 }}>Wallet balance</span>
              {confirmModal.type === 'approve' ? (
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--g600)' }}>
                  ₹{walletBalance.toLocaleString('en-IN')}
                  <span style={{ margin: '0 5px', color: 'var(--g300)' }}>→</span>
                  <span style={{ color: 'var(--rd)', fontWeight: 700 }}>₹{afterBal.toLocaleString('en-IN')}</span>
                </span>
              ) : (
                <span style={{ fontSize: 12, fontWeight: 600, color: '#0A9B5F' }}>No deduction</span>
              )}
            </div>
          </div>

          {/* Body */}
          <div style={{ padding: '0 22px 16px', fontSize: 12.5, color: 'var(--g600)', lineHeight: 1.65 }}>
            {confirmModal.type === 'approve' ? (
              <>
                <p style={{ margin: '0 0 10px' }}>⚠️ You are about to <strong style={{ color: 'var(--nv)' }}>approve</strong> the additional charge of <strong style={{ color: 'var(--nv)' }}>{modalTotalFmt}</strong> for this check. This amount will be immediately deducted from your wallet.</p>
                <p style={{ margin: 0 }}>This action <strong style={{ color: 'var(--nv)' }}>cannot be undone</strong>. The check will resume once approved.</p>
              </>
            ) : (
              <span>⚠️ You are about to <strong style={{ color: 'var(--nv)' }}>reject</strong> this additional charge. The check will be <strong style={{ color: 'var(--rd)' }}>cancelled</strong> and cannot be restarted without a new order. No credits will be deducted. This action <strong style={{ color: 'var(--nv)' }}>cannot be undone</strong>.</span>
            )}
          </div>

          {/* Actions */}
          <div style={{ padding: '14px 22px 18px', borderTop: '1px solid var(--g100)', display: 'flex', gap: 9, justifyContent: 'flex-end' }}>
            <button onClick={() => setConfirmModal(null)} style={{ padding: '9px 18px', borderRadius: 8, border: '1.5px solid var(--g200)', background: 'var(--wh)', fontFamily: 'var(--fn)', fontSize: 12.5, fontWeight: 600, color: 'var(--g600)', cursor: 'pointer' }}>Cancel</button>
            <button disabled={actionLoading} onClick={() => handleChargeAction(confirmModal.type, confirmModal.charge)}
              style={{ padding: '9px 20px', borderRadius: 8, border: 'none', fontFamily: 'var(--fn)', fontSize: 12.5, fontWeight: 700, cursor: actionLoading ? 'default' : 'pointer', background: confirmModal.type === 'approve' ? '#0DB36B' : '#E53E3E', color: '#fff', opacity: actionLoading ? 0.7 : 1 }}>
              {actionLoading ? <span className="spinner" style={{ width: 14, height: 14, borderColor: 'rgba(255,255,255,0.3)', borderTopColor: '#fff' }} /> : confirmModal.type === 'approve' ? `✅ Yes, Approve & Deduct ${modalTotalFmt}` : '🚫 Yes, Reject This Check'}
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Add Check Confirm Modal */}
    {addConfirmOpen && (
      <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(26,31,54,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(3px)' }}
        onClick={() => !addSubmitting && setAddConfirmOpen(false)}>
        <div style={{ background: 'var(--wh)', borderRadius: 16, boxShadow: '0 16px 48px rgba(26,31,54,.22)', width: '100%', maxWidth: 420, margin: 20, maxHeight: 'calc(100vh - 40px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
          onClick={e => e.stopPropagation()}>

          <div style={{ padding: '18px 22px 14px', borderBottom: '1px solid var(--g100)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 38, height: 38, borderRadius: 10, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, background: 'var(--or-lt)' }}>➕</div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--nv)' }}>Confirm Add Check</div>
                <div style={{ fontSize: 11, color: 'var(--g400)', marginTop: 2 }}>{order.ref}{order.name !== '—' ? ` · ${order.name}` : ''}</div>
              </div>
            </div>
            <button onClick={() => !addSubmitting && setAddConfirmOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: 'var(--g400)', padding: 2, lineHeight: 1 }}>✕</button>
          </div>

          <div style={{ margin: '14px 22px 0', flex: '1 1 auto', minHeight: 0, overflowY: 'auto', background: 'var(--g50)', border: '1px solid var(--g200)', borderRadius: 9, padding: '0 14px' }}>
            {addCheckList.map((c, i) => {
              const qty = addQtys[i] || 0;
              if (!qty) return null;
              return (
                <div key={c.name} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--g100)' }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--nv)' }}>{qty}x {c.name}</span>
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--or-dk)' }}>₹{(qty * c.price).toLocaleString('en-IN')}</span>
                </div>
              );
            })}
          </div>

          <div style={{ margin: '0 22px', padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
            <span style={{ fontSize: 11, color: 'var(--g400)', fontWeight: 600 }}>Wallet balance</span>
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--g600)' }}>
              ₹{walletBalance.toLocaleString('en-IN')}
              <span style={{ margin: '0 5px', color: 'var(--g300)' }}>→</span>
              <span style={{ color: 'var(--rd)', fontWeight: 700 }}>₹{addCheckAfterBal.toLocaleString('en-IN')}</span>
            </span>
          </div>

          <div style={{ padding: '0 22px 16px', fontSize: 12.5, color: 'var(--g600)', lineHeight: 1.65, flexShrink: 0 }}>
            ⚠️ You are about to add these checks to this profile. <strong style={{ color: 'var(--nv)' }}>₹{addCheckTotal.toLocaleString('en-IN')}</strong> will be immediately deducted from your wallet. This action <strong style={{ color: 'var(--nv)' }}>cannot be undone</strong>.
          </div>

          <div style={{ padding: '14px 22px 18px', borderTop: '1px solid var(--g100)', display: 'flex', gap: 9, justifyContent: 'flex-end', flexShrink: 0 }}>
            <button disabled={addSubmitting} onClick={() => setAddConfirmOpen(false)} style={{ padding: '9px 18px', borderRadius: 8, border: '1.5px solid var(--g200)', background: 'var(--wh)', fontFamily: 'var(--fn)', fontSize: 12.5, fontWeight: 600, color: 'var(--g600)', cursor: 'pointer' }}>Cancel</button>
            <button disabled={addSubmitting} onClick={submitAddChecks}
              style={{ padding: '9px 20px', borderRadius: 8, border: 'none', fontFamily: 'var(--fn)', fontSize: 12.5, fontWeight: 700, cursor: addSubmitting ? 'default' : 'pointer', background: '#0DB36B', color: '#fff', opacity: addSubmitting ? 0.7 : 1 }}>
              {addSubmitting ? <span className="spinner" style={{ width: 14, height: 14, borderColor: 'rgba(255,255,255,0.3)', borderTopColor: '#fff' }} /> : `✅ Yes, Add & Deduct ₹${addCheckTotal.toLocaleString('en-IN')}`}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
